interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitter: boolean;
  retryCondition?: (error: any) => boolean;
}

interface RetryAttempt {
  attempt: number;
  timestamp: number;
  error: any;
  delay: number;
}

interface RetryContext {
  operation: string;
  attempts: RetryAttempt[];
  config: RetryConfig;
  startTime: number;
}

class IntelligentRetryService {
  private contexts = new Map<string, RetryContext>();
  private defaultConfig: RetryConfig = {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 30000,
    backoffMultiplier: 2,
    jitter: true,
    retryCondition: (error) => this.isRetryableError(error)
  };

  async executeWithRetry<T>(
    operation: () => Promise<T>,
    operationId: string,
    config?: Partial<RetryConfig>
  ): Promise<T> {
    const finalConfig = { ...this.defaultConfig, ...config };
    const context: RetryContext = {
      operation: operationId,
      attempts: [],
      config: finalConfig,
      startTime: Date.now()
    };

    this.contexts.set(operationId, context);

    try {
      return await this.attemptOperation(operation, context);
    } finally {
      // Keep context for analysis but clean up old ones
      setTimeout(() => this.contexts.delete(operationId), 300000); // 5 minutes
    }
  }

  private async attemptOperation<T>(
    operation: () => Promise<T>,
    context: RetryContext
  ): Promise<T> {
    for (let attempt = 1; attempt <= context.config.maxAttempts; attempt++) {
      try {
        const result = await operation();
        
        // Success - log if there were previous failures
        if (context.attempts.length > 0) {
          console.log(`Operation ${context.operation} succeeded after ${attempt} attempts`);
        }
        
        return result;
      } catch (error) {
        const attemptRecord: RetryAttempt = {
          attempt,
          timestamp: Date.now(),
          error,
          delay: 0
        };

        context.attempts.push(attemptRecord);

        // Check if we should retry
        if (attempt >= context.config.maxAttempts || 
            !context.config.retryCondition?.(error)) {
          
          this.logFailure(context);
          throw error;
        }

        // Calculate delay for next attempt
        const delay = this.calculateDelay(attempt, context.config);
        attemptRecord.delay = delay;

        console.warn(`Operation ${context.operation} failed (attempt ${attempt}/${context.config.maxAttempts}). Retrying in ${delay}ms`, error);

        await this.sleep(delay);
      }
    }

    throw new Error(`Operation ${context.operation} failed after ${context.config.maxAttempts} attempts`);
  }

  private calculateDelay(attempt: number, config: RetryConfig): number {
    // Exponential backoff with jitter
    let delay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1);
    
    // Apply maximum delay limit
    delay = Math.min(delay, config.maxDelay);
    
    // Add jitter to prevent thundering herd
    if (config.jitter) {
      const jitterAmount = delay * 0.1; // 10% jitter
      delay += (Math.random() - 0.5) * 2 * jitterAmount;
    }
    
    return Math.round(delay);
  }

  private isRetryableError(error: any): boolean {
    // Network errors
    if (error.name === 'NetworkError' || error.message?.includes('network')) {
      return true;
    }

    // HTTP status codes that are retryable
    if (error.status) {
      const retryableStatuses = [408, 429, 500, 502, 503, 504];
      return retryableStatuses.includes(error.status);
    }

    // Supabase specific errors
    if (error.code) {
      const retryableCodes = ['PGRST301', 'PGRST302']; // Connection errors
      return retryableCodes.includes(error.code);
    }

    // Timeout errors
    if (error.message?.includes('timeout') || error.message?.includes('TIMEOUT')) {
      return true;
    }

    // Rate limiting
    if (error.message?.includes('rate limit') || error.message?.includes('too many requests')) {
      return true;
    }

    return false;
  }

  private logFailure(context: RetryContext): void {
    const totalTime = Date.now() - context.startTime;
    const lastError = context.attempts[context.attempts.length - 1]?.error;
    
    console.error(`Operation ${context.operation} failed permanently after ${context.attempts.length} attempts in ${totalTime}ms`, {
      attempts: context.attempts,
      lastError
    });

    // Send to monitoring if available
    this.sendToMonitoring(context);
  }

  private sendToMonitoring(context: RetryContext): void {
    // This would integrate with your monitoring service
    const event = {
      type: 'retry_failure',
      operation: context.operation,
      attempts: context.attempts.length,
      totalTime: Date.now() - context.startTime,
      errors: context.attempts.map(a => ({
        attempt: a.attempt,
        error: a.error.message || a.error,
        timestamp: a.timestamp
      }))
    };

    // Example: Send to analytics or monitoring service
    try {
      // backgroundSyncService.addTask('analytics', { events: [event] }, 'low');
    } catch (error) {
      console.warn('Failed to send retry failure to monitoring:', error);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Specialized retry methods for common operations
  async retryApiCall<T>(
    apiCall: () => Promise<T>,
    operationId: string
  ): Promise<T> {
    return this.executeWithRetry(apiCall, `api_${operationId}`, {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      retryCondition: (error) => {
        // More aggressive retry for API calls
        return this.isRetryableError(error) || error.status >= 500;
      }
    });
  }

  async retryDatabaseOperation<T>(
    dbOperation: () => Promise<T>,
    operationId: string
  ): Promise<T> {
    return this.executeWithRetry(dbOperation, `db_${operationId}`, {
      maxAttempts: 5,
      baseDelay: 500,
      maxDelay: 5000,
      backoffMultiplier: 1.5,
      retryCondition: (error) => {
        // Database-specific retry logic
        return this.isRetryableError(error) || 
               error.message?.includes('connection') ||
               error.message?.includes('timeout');
      }
    });
  }

  async retryWithCircuitBreaker<T>(
    operation: () => Promise<T>,
    operationId: string,
    circuitBreakerThreshold = 5
  ): Promise<T> {
    const recentFailures = this.getRecentFailures(operationId, 60000); // Last minute
    
    if (recentFailures >= circuitBreakerThreshold) {
      throw new Error(`Circuit breaker open for operation ${operationId}. Too many recent failures.`);
    }

    return this.executeWithRetry(operation, operationId);
  }

  private getRecentFailures(operationId: string, timeWindow: number): number {
    const now = Date.now();
    let failures = 0;

    this.contexts.forEach((context, id) => {
      if (id.includes(operationId) && 
          context.attempts.length > 0 && 
          now - context.startTime < timeWindow) {
        failures += context.attempts.length;
      }
    });

    return failures;
  }

  // Public methods for monitoring and management
  getRetryStats() {
    const stats = {
      activeOperations: this.contexts.size,
      totalAttempts: 0,
      failedOperations: 0,
      successfulRetries: 0
    };

    this.contexts.forEach(context => {
      stats.totalAttempts += context.attempts.length;
      if (context.attempts.length > 0) {
        if (context.attempts.length >= context.config.maxAttempts) {
          stats.failedOperations++;
        } else {
          stats.successfulRetries++;
        }
      }
    });

    return stats;
  }

  clearOldContexts(): void {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    
    this.contexts.forEach((context, id) => {
      if (context.startTime < oneHourAgo) {
        this.contexts.delete(id);
      }
    });
  }

  getFailurePatterns(): { operation: string; failureRate: number; avgAttempts: number }[] {
    const patterns = new Map<string, { total: number; attempts: number }>();

    this.contexts.forEach(context => {
      const baseOperation = context.operation.split('_')[0];
      const existing = patterns.get(baseOperation) || { total: 0, attempts: 0 };
      
      patterns.set(baseOperation, {
        total: existing.total + 1,
        attempts: existing.attempts + context.attempts.length
      });
    });

    return Array.from(patterns.entries()).map(([operation, data]) => ({
      operation,
      failureRate: data.attempts / data.total,
      avgAttempts: data.attempts / data.total
    }));
  }
}

export const intelligentRetryService = new IntelligentRetryService();
export type { RetryConfig, RetryContext };
